import { useEffect, useRef, useState, useCallback } from "react";
import images from "../../assets/images";
import './DownloadsList.scss';
import ImageViewer from "../../components/ImageViewer/ImageViewer";
import PDFViewer from "../../components/PDFViewer/PDFViewer";
import FileViewer from "../../components/FileViewer/FileViewer";
import CsvViewer from "../../components/CsvViewer/CsvViewer";
import EpubFileViewer from "../../components/EpubFileViewer/EpubFileViewer";
import SingleAudioPlay from "../../components/SingleAudioPlay/SingleAudioPlay";
import ConfirmationPopup from "../../components/ConfirmationPopup/ConfirmationPopup";
import Loader from "../../components/Loader/Loader";
import { deleteMaterial, fetchMaterials } from "../../database";
import { useAppSelector } from "../../redux/app/hooks";
import { formatFileSize } from "../../utils/helper";
import moment from "moment";


interface CordovaFileDetail {
  fileId: string;
  userId: string;
  filename: string;
  filetype?: string;
  filepath: string;
  filesize: number;
  filedata: string;
  fileLocation?: string;
  subject?: string;
  timestamp: string;
}

const DownloadsList = () => {
  const {
    pdfImg,
    DeleteGif,
    csvImg,
    docsImg,
    excelPreview,
    powerpoint,
    epub,
    backArrow,
    imageSvg,
    videoPlay,
    audioSvg
  } = images;

  const [fileDetails, setFileDetails] = useState<CordovaFileDetail[]>([]);
  const [displayView, setDisplayView] = useState<boolean>(false);
  const [fileContent, setFileContent] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<CordovaFileDetail | null>(null);
  const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
  const [totalFiles, setTotalFiles] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const profile = useAppSelector((state) => state.auth.adminProfile);
  const userId = profile?.getCurrentUser?.nodes[0]?.uId

  const fileTypeIcons: { [key: string]: string } = {
    pdf: pdfImg,
    csv: csvImg,
    doc: docsImg,
    docs: docsImg,
    docx: docsImg,
    xls: excelPreview,
    xlsx: excelPreview,
    ppt: powerpoint,
    pptx: powerpoint,
    epub: epub,
    image: imageSvg,
    video: videoPlay,
    audio: audioSvg
  };

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const totalPages = Math.ceil(totalFiles / itemsPerPage);

  const getInAppDownloadsList = useCallback(async (page: number = 1, limit: number = 10, userId: string) => {
    setIsLoading(true);
    try {
      const response = await fetchMaterials(page, limit, userId);
      console.log(response, '=====fetchMaterialsResponse');
      if (response) {
        handleGetMaterialsResponse({ success: true, ...response });
      }
    } catch (err) {
      setIsLoading(false);
      console.error('Error fetching materials:', err);
    }
  }, []);

  // Handle the response for fetched materials
  const handleGetMaterialsResponse = (response: any) => {
    console.log(response, '======response from db');

    if (response.success) {
      if (currentPage === 1) {
        setFileDetails(response.materials);
      } else {
        setFileDetails(prevDetails => [...prevDetails, ...response.materials]);
      }
      setTotalFiles(response.total);
    } else {
      console.error('Error fetching materials:', response.error);
    }
    setIsLoading(false);
  };

  const formatTimestamp = (timestamp: any) => {

    const localDate = moment.utc(timestamp).local();  // Convert from UTC to local timezone

    // Format the date in the desired format
    const day = localDate.format('DD');
    const month = localDate.format('MMM');  // Short month name
    const year = localDate.format('YYYY');
    const time = localDate.format('h:mm A');

    return `${day}-${month}-${year} at ${time}`;
};

  useEffect(() => {
    console.log("[useEffect] currentPage:", currentPage);
    if (currentPage > 0 && userId) {
      getInAppDownloadsList(currentPage, itemsPerPage, userId);
    }
  }, [currentPage, getInAppDownloadsList, itemsPerPage]);

  const handleFileClick = (file: CordovaFileDetail) => {
    const arrayOfStrings = file.filedata.split(',');
    const arrayOfNumbers = arrayOfStrings.map(Number);
    const byteArray = new Uint8Array(arrayOfNumbers);
    if(byteArray){
      const fileDataBlob = new Blob([byteArray], { type: file.filetype });
      console.log(fileDataBlob, '======fileDataBlob');
      const fileBlobUrl = URL.createObjectURL(fileDataBlob);
      console.log(fileBlobUrl, '=========fileBlobUrl');
      setFileContent(fileBlobUrl);
      setSelectedFile(file);
      setDisplayView(true);  
    }
  };

  const handleCloseViewer = () => {
    setDisplayView(false);
    setFileContent('');
    setSelectedFile(null);
  };

  const [showNameTooltip, setShowNameTooltip] = useState<boolean[]>([]);
  const [showCategoryTooltip, setShowCategoryTooltip] = useState<boolean[]>([]);
  const columnRefs = useRef<(HTMLTableCellElement | null)[]>([]);

  const checkOverflow = (index: number): boolean => {
    const element = columnRefs.current[index];
    if (element) {
      return element.scrollWidth > element.clientWidth;
    }
    return false;
  };

  const updateTooltips = useCallback(() => {
    const nameTooltips = fileDetails.map((_, rowIndex) =>
      checkOverflow(rowIndex * 7)
    );
    const categoryTooltips = fileDetails.map((_, rowIndex) =>
      checkOverflow(rowIndex * 7 + 4)
    );

    setShowNameTooltip(nameTooltips);
    setShowCategoryTooltip(categoryTooltips);
  }, [fileDetails]);

  useEffect(() => {
    updateTooltips();
  }, [updateTooltips]);

  useEffect(() => {
    const handleResize = () => {
      updateTooltips();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [updateTooltips]);

  const handleNextPage = () => {
    if (currentPage < Math.ceil(totalFiles / itemsPerPage) && !isLoading) {
      console.log("Next page clicked, currentPage:", currentPage + 1);  // Debug log
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1 && !isLoading) {
      console.log("Previous page clicked, currentPage:", currentPage - 1);  // Debug log
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const handleDeleteMaterial = async (fileId: string) => {
    console.log(fileId, '====fileId in delete');
    try {
      await deleteMaterial(fileId);
      console.log(`Material with fileId ${fileId} deleted.`);
      setFileDetails(prevDetails => prevDetails.filter(file => file.fileId !== fileId));
      setTotalFiles(prevTotal => prevTotal - 1);
      if(fileDetails.length === 1 && currentPage > 1){
        setCurrentPage(currentPage -1);
      await getInAppDownloadsList(currentPage-1, itemsPerPage, userId);
      }else{
        await getInAppDownloadsList(currentPage, itemsPerPage, userId);
      }
    } catch (error) {
      console.error('Error deleting material:', error);
    }
  };

  const handleDelete = (file: CordovaFileDetail) => {
    setOpenConfirmationPopup(true);
    setSelectedFile(file);
  };

  const onYesButtonClick = () => {
    setOpenConfirmationPopup(false);
    if (selectedFile) {
      handleDeleteMaterial(selectedFile?.fileId);
    }
  };

  const renderViewer = () => {
    if (!selectedFile) return null;

    // Rendering logic for different file types
    switch (selectedFile.filetype) {
      case 'video':
        return (
          <video
            src={fileContent}
            ref={videoRef}
            autoPlay
            disablePictureInPicture
            controls
            controlsList='nodownload noplaybackrate'
            loop
            className='detailVideoTagStyle'
            style={{ display: 'flex', outline: 'none', border: 'none' }}
          />
        );
      case 'audio':
        return <SingleAudioPlay podCastPDFFilePath={fileContent} />;
      case 'image':
        return <ImageViewer file={fileContent} onClose={handleCloseViewer} imgFileData={selectedFile} type="Images" />;
      case 'epub':
        return <EpubFileViewer fileUrl={fileContent} fileData={selectedFile} />;
      case 'pdf':
        return <PDFViewer file={fileContent} pdfFileData={selectedFile} onClose={handleCloseViewer} type="Teaching Material" />;
      case 'csv':
      case 'excel':
        return <CsvViewer fileUrl={fileContent} fileData={selectedFile} onClose={handleCloseViewer} />;
      case 'docs':
        return <PDFViewer file={fileContent} pdfFileData={selectedFile} onClose={handleCloseViewer} type="Teaching Material" />;
        // return (
        //   <FileViewer
        //     fileUrl={fileContent}
        //     fileData={selectedFile}
        //     onClose={handleCloseViewer}
        //     fileType={selectedFile?.filetype}
        //   />
        // );
      case 'ppt':
        return <PDFViewer file={fileContent} pdfFileData={selectedFile} onClose={handleCloseViewer} type="Teaching Material" />;
        // return (
        //   <FileViewer
        //     fileUrl={fileContent}
        //     fileData={selectedFile}
        //     onClose={handleCloseViewer}
        //     fileType={selectedFile?.filetype}
        //   />
        // );
      default:
        return null;
    }
  };

  const fileTypeMap: { [key: string]: string } = {
    pdf: "PDF Document",
    xlsx: "Microsoft Excel Document",
    xls: "Microsoft Excel Document",
    csv: "CSV Document",
    epub: "EPUB",
    doc: "Microsoft Word Document",
    docx: "Microsoft Word Document",
    ppt: "Microsoft PowerPoint Document",
    pptx: "Microsoft PowerPoint Document",
    jpg: "JPEG Image",
    jpeg: "JPEG Image",
    png: "PNG Image",
    svg: "SVG Document",
    mp3: "MP3 Audio",
    wav: "WAV Audio",
    ogg: "OGG Audio",
    mp4: "MP4 Video",
    avi: "AVI Video",
  };

  const getFileExtension = (filepath: string): string => {
    if (!filepath) return ''; // Return empty if no filepath
  
    const parts = filepath.split('.');
    return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : ''; // Return the extension in lowercase
  };

  return (
    <div
      className={`${fileDetails.length > 0 ? (selectedFile && (selectedFile?.filetype === 'excel' ||selectedFile?.filetype === 'csv')) ? "csvdownloadsListContainer" : "downloadsListContainer" : "noData"
        }`}
      style={{ padding: "20px", gap: '40px'}}
    >
      {isLoading ? <Loader />
        : displayView && selectedFile ? (
          <div className="ElectronviewContainer">
            <div className="viewHeader">
              <img
                src={backArrow}
                onClick={handleCloseViewer}
                alt="backArrow"
                className="backIcon"
              />
            </div>
            <div className={selectedFile?.filetype === 'video' || selectedFile?.filetype === 'excel' ||selectedFile?.filetype === 'csv' ? "viewerContainerColumn" :"viewerContainer"}>
              {renderViewer()}
            </div>
            {/* {renderViewer()} */}
          </div>
        ) : fileDetails.length > 0 ? (
          <div className="downloads-table-container">
            <table className="downloads-table">
              <thead>
                <tr>
                  <th className="name-column-heading">Name</th>
                  <th className="date-modified-column">Date</th>
                  <th className="type-column">Type</th>
                  <th className="size-column">Size</th>
                  <th className="category-column">Category</th>
                  <th className="subject-column">Subject</th>
                  <th className="actions-column"></th>
                </tr>
              </thead>
              <tbody className="table-body">
              {fileDetails.map((file, index) => (
                <tr key={index} className="downloads-row">
                  <td
                    className="name-column"
                    onClick={() => handleFileClick(file)}
                  >
                    {fileTypeIcons[file.filetype === "csv" ? getFileExtension(file?.filepath) : (file.filetype?.toLowerCase() || file.filepath.split('.').pop()?.toLowerCase() || '')] && (
                       <img
                       src={fileTypeIcons[file.filetype === "csv" ? getFileExtension(file?.filepath) : (file.filetype?.toLowerCase() || file.filepath.split('.').pop()?.toLowerCase() || '')]}
                       alt={file.filetype}
                        className="file-icon"
                      />
                    )}
                    {file.filename ? file.filename : "-"}
                  </td>
                  <td className="date-modified-column">{file.timestamp ? formatTimestamp(file.timestamp):"-"}</td>
                  {/* <td className="type-column">{file.filetype ? file.filetype === "csv" ? getFileExtension(file?.filepath):fileTypeMap[file.filetype]: '-'}</td> */}
                  <td className="type-column">{file.filetype ? fileTypeMap[getFileExtension(file.filepath)]: '-'}</td>
                  <td className="size-column">{file.filesize ? formatFileSize(file.filesize):"-"}</td>
                  <td className="category-column">{file.fileLocation ? file.fileLocation : "-"}</td>
                  <td className="subject-column">{file.subject ? file.subject:"-"}</td>
                  <td className="actions-column">
                    <img
                      src={images.deleteIcon}
                      alt="deleteIcon"
                      className="deleteIcon"
                      onClick={() => {
                        setSelectedFile(file);
                        setOpenConfirmationPopup(true);
                      }}
                    />
                  </td>
                </tr>
              ))}

              </tbody>
            </table>
            {/* <div className="pagination">
              <button onClick={handlePrevPage} disabled={currentPage === 1} className="prevBtn">
                Prev
              </button>
              <span className="currentPage">
                Page {currentPage} of {totalPages}
              </span>
              <button onClick={handleNextPage} disabled={currentPage === totalPages} className="nextBtn">
                Next
              </button>
            </div> */}
          </div>
        ) : (
          <div className="noDataFoundStyle">No data found</div>
        )}
        {(!isLoading && fileDetails?.length > 0 && !displayView) && <div className="pagination">
            <button onClick={handlePrevPage} disabled={currentPage === 1} className={currentPage === 1 ? "disableprevBtn" : "prevBtn"}>
              Prev
            </button>
            <span className="currentPage">
              Page {currentPage} of {totalPages}
            </span>
            <button onClick={handleNextPage} disabled={currentPage >= totalPages} className={currentPage >= totalPages ? "disablenextBtn" : "nextBtn"}>
              Next
            </button>
          </div>}
      <ConfirmationPopup
        open={openConfirmationPopup}
        message={`Are you sure you want to delete this file?`}
        gifData={DeleteGif}
        yesButtonFunctionality={onYesButtonClick}
        noButtonFunctionality={() => setOpenConfirmationPopup(false)}
        messageWidth={"195px"}
      />
    </div>
  );
};

export default DownloadsList;


